import { useState, useEffect, useRef } from 'react';
import {
  collection,
  query,
  orderBy,
  where,
  limit,
  getDocs
} from 'firebase/firestore';
import { db } from '../firebase/config-v9';

const CACHE_TTL_MS = 5 * 60 * 1000;
const CACHE_KEYS = {
  products: 'homepage_products_cache_v1',
  categories: 'homepage_categories_cache_v1',
  announcements: 'homepage_announcements_cache_v1'
};

const readCache = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.timestamp || !Array.isArray(parsed.data)) return null;
    if (Date.now() - parsed.timestamp > CACHE_TTL_MS) return null;
    return parsed.data;
  } catch (e) {
    return null;
  }
};

const writeCache = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify({
      data,
      timestamp: Date.now()
    }));
  } catch (e) {
    // Ignore cache errors
  }
};

export const useHomepageData = () => {
  const cachedProducts = readCache(CACHE_KEYS.products) || [];
  const cachedCategories = readCache(CACHE_KEYS.categories) || [];
  const cachedAnnouncements = readCache(CACHE_KEYS.announcements) || [];

  const [data, setData] = useState({
    products: cachedProducts,
    categories: cachedCategories,
    announcements: cachedAnnouncements
  });
  const [loading, setLoading] = useState(
    cachedProducts.length === 0 &&
    cachedCategories.length === 0 &&
    cachedAnnouncements.length === 0
  );
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    const fetchHomepageData = async () => {
      if (!db) {
        setError('Firestore not initialized');
        setLoading(false);
        return;
      }

      console.time('homepage-firestore-fetch');
      try {
        const productsQuery = query(
          collection(db, 'products'),
          orderBy('createdAt', 'desc')
        );
        const categoriesQuery = query(
          collection(db, 'categories'),
          orderBy('displayOrder', 'asc')
        );
        const announcementsQuery = query(
          collection(db, 'announcements'),
          where('isActive', '==', true),
          orderBy('createdAt', 'desc'),
          limit(1)
        );

        const [productsSnap, categoriesSnap, announcementsSnap] = await Promise.all([
          getDocs(productsQuery),
          getDocs(categoriesQuery),
          getDocs(announcementsQuery)
        ]);

        const products = productsSnap.docs.map((snap) => ({
          id: snap.id,
          ...snap.data()
        }));
        const categories = categoriesSnap.docs.map((snap) => ({
          docId: snap.id,
          ...snap.data(),
          id: snap.data()?.id || snap.id
        }));
        const announcements = announcementsSnap.docs.map((snap) => ({
          id: snap.id,
          ...snap.data()
        }));

        if (!mountedRef.current) return;

        setData({ products, categories, announcements });
        writeCache(CACHE_KEYS.products, products);
        writeCache(CACHE_KEYS.categories, categories);
        writeCache(CACHE_KEYS.announcements, announcements);
        setError(null);
      } catch (err) {
        if (mountedRef.current) {
          setError(err?.message || 'Failed to load homepage data');
        }
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
        console.timeEnd('homepage-firestore-fetch');
      }
    };

    fetchHomepageData();

    return () => {
      mountedRef.current = false;
    };
  }, []);

  return {
    ...data,
    loading,
    error
  };
};
